import {
  MapPinIcon,
  ShieldCheckIcon,
  TagIcon,
} from '@heroicons/react/24/outline';

import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { DetailedPoliceReport } from '@/services/api/generated/policeReportsService';

interface PoliceDetailsHeaderProps {
  report: DetailedPoliceReport;
}

export const PoliceDetailsHeader = ({ report }: PoliceDetailsHeaderProps) => {
  const { police_department: department } = report;

  return (
    <div className="space-y-3">
      <div className="flex items-center space-x-3">
        <ShieldCheckIcon className="h-6 w-6 text-gray-400" />
        <div className="min-w-0 flex-1">
          <h1 className="text-xl font-semibold text-gray-900 truncate">
            {department.name || 'Unknown Department'}
          </h1>
        </div>
      </div>

      {/* Department Context - Responsive Row */}
      <div className="ml-9">
        <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-sm">
          {/* Location */}
          {(department.state_name || department.state_code) && (
            <div className="flex items-center space-x-1 text-gray-600">
              <MapPinIcon className="h-4 w-4" />
              <span>
                {department.state_name
                  ? `${department.state_name} (${department.state_code})`
                  : department.state_code}
              </span>
            </div>
          )}

          {/* Jurisdiction */}
          {department.jurisdiction_type && (
            <span className="inline-flex items-center rounded-full bg-gray-100 px-2 py-1 text-xs font-medium text-gray-700">
              {department.jurisdiction_type
                .replace('_', ' ')
                .toLowerCase()
                .replace(/\b\w/g, (l) => l.toUpperCase())}
              {department.jurisdiction_name &&
                ` • ${department.jurisdiction_name}`}
            </span>
          )}

          {/* Aliases with Tooltip */}
          {department.aliases && department.aliases.length > 0 && (
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <div className="flex items-center space-x-1 cursor-help text-gray-500">
                    <TagIcon className="h-4 w-4" />
                    <span className="text-xs">
                      {department.aliases.length} alias
                      {department.aliases.length > 1 ? 'es' : ''}
                    </span>
                  </div>
                </TooltipTrigger>
                <TooltipContent side="bottom" className="max-w-xs">
                  <div className="space-y-1">
                    <div className="text-sm font-medium">Also known as:</div>
                    <div className="flex flex-wrap gap-1">
                      {department.aliases.map((alias, index) => (
                        <span
                          key={index}
                          className="text-xs bg-gray-100 text-gray-700 px-2 py-0.5 rounded"
                        >
                          {alias}
                        </span>
                      ))}
                    </div>
                  </div>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}
        </div>
      </div>
    </div>
  );
};
